
import { t, Trans } from '@lingui/macro';
import { RecordingData } from 'capacitor-voice-recorder';
import EventEmitter from 'events';
import { Loader2, Check, Undo2, Mic } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { createPortal } from "react-dom";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { useSpeechToText } from '../../../hooks/useSpeechToText';
import { polishDictation } from '../../../services/AiMemoAnalyzer';
import { My } from '../../../services/My';
import { IconSparkles } from '../FigmaIcons';

export type RecordingAction = 'send-text' | 'cancel';
interface IMsgRecordModal {
  onAction: (action: RecordingAction, text?: string, recording?: RecordingData) => void;
  customButton?: React.ReactNode;
};

type ModalState = 'idle' | 'recording' | 'polishing' | 'review';

const MsgRecordModal: React.FC<IMsgRecordModal> = ({ onAction, customButton }) => {
  const [show, setShow] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('idle');
  const [rawText, setRawText] = useState('');
  const [polishedText, setPolishedText] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [footerDimensions, setFooterDimensions] = useState<{ width: number; left: number } | null>(null);

  // Audio recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingDataRef = useRef<RecordingData | undefined>(undefined);
  const recordStartTimeRef = useRef<number>(0);

  const defaultLang = My.user?.lang === 'ja' ? 'ja-JP' : My.user?.lang === 'en' ? 'en-US' : 'ko-KR';

  const {
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechToText({ lang: defaultLang });

  // Subscribe emitter
  useEffect(() => {
    VoiceRecordModalEmitter.subscribe(() => showModal());
  }, []);

  // Footer dimensions
  useEffect(() => {
    const updateDimensions = () => {
      const footer = document.querySelector('#msg-input-footer-container');
      if (footer) {
        const rect = footer.getBoundingClientRect();
        setFooterDimensions({ width: rect.width, left: rect.left });
      }
    };
    if (show) {
      updateDimensions();
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    }
  }, [show]);

  // ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) handleClose();
    };
    if (show) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [show]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  function showModal() {
    setShow(true);
    setModalState('idle');
    setRawText('');
    setPolishedText('');
    setShowRaw(false);
    setElapsedTime(0);
    resetTranscript();
    recordingDataRef.current = undefined;
    audioChunksRef.current = [];
  }

  function handleClose() {
    if (modalState === 'recording') {
      stopListening();
      stopMediaRecorder();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
      setModalState('idle');
      onAction('cancel');
    }, 300);
  }

  async function startMediaRecorder() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 48000 }
      });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, options);

      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      recordStartTimeRef.current = Date.now();
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        console.info('[MediaRecorder] 마이크 권한이 허용되지 않았습니다.');
      } else {
        console.warn('[MediaRecorder] Failed:', err);
      }
    }
  }

  function stopMediaRecorder(): Promise<RecordingData | undefined> {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(undefined);
        return;
      }

      recorder.onstop = async () => {
        const msDuration = Date.now() - recordStartTimeRef.current;
        const actualMime = recorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: actualMime });

        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1] || '';
          const data: RecordingData = {
            value: {
              recordDataBase64: base64,
              mimeType: actualMime,
              msDuration,
            }
          };
          recordingDataRef.current = data;
          resolve(data);
        };
        reader.readAsDataURL(blob);

        // Stop stream tracks
        recorder.stream.getTracks().forEach(t => t.stop());
      };

      recorder.stop();
    });
  }

  async function handleStartRecording() {
    setModalState('recording');
    setRawText('');
    setPolishedText('');
    setElapsedTime(0);
    resetTranscript();
    recordingDataRef.current = undefined;
    audioChunksRef.current = [];

    // Start both: speech recognition + audio recording
    startListening();
    await startMediaRecorder();

    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
  }

  async function handleStopRecording() {
    stopListening();
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop audio recording and get data
    await stopMediaRecorder();

    const finalText = transcript.trim();
    setRawText(finalText);

    if (!finalText) {
      setModalState('idle');
      return;
    }

    // AI 교정
    setModalState('polishing');
    try {
      const polished = await polishDictation(finalText, defaultLang);
      setPolishedText(polished);
    } catch {
      setPolishedText(finalText);
    }
    setModalState('review');
  }

  function handleSend() {
    const textToSend = showRaw ? rawText : polishedText;
    if (textToSend.trim()) {
      const rec = recordingDataRef.current;
      if (rec) {
        const b64Len = rec.value.recordDataBase64?.length || 0;
        console.log(`[MsgRecord] Recording: ${(b64Len / 1024 / 1024).toFixed(2)} MB base64, mimeType=${rec.value.mimeType}, duration=${rec.value.msDuration}ms`);
      }
      onAction('send-text', textToSend.trim(), recordingDataRef.current);
    }
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      setIsClosing(false);
      setModalState('idle');
    }, 300);
  }

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-overlay z-[1900]",
          isClosing ? "animate-out fade-out-0 duration-300" : "animate-in fade-in-0"
        )}
        onClick={handleClose}
      />

      {/* Bottom Sheet */}
      <div
        className={cn(
          "fixed bottom-0 z-[2000] bg-background rounded-t-xl border-t border-l border-r shadow-lg p-5",
          isClosing ? "animate-out slide-out-to-bottom duration-300" : "animate-in slide-in-from-bottom duration-300"
        )}
        style={footerDimensions ? {
          width: `${footerDimensions.width}px`,
          left: `${footerDimensions.left}px`
        } : undefined}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[color:rgb(var(--text-title))]">
              <Trans>Record & Recognize</Trans>
            </span>
            <Button variant="ghost" size="sm" onClick={handleClose} className="h-7 px-2 text-xs text-muted-foreground">
              <Trans>Close</Trans>
            </Button>
          </div>

          {/* === IDLE === */}
          {modalState === 'idle' && (
            <div className="flex flex-col items-center gap-4 py-6">
              <button
                onClick={handleStartRecording}
                className="w-20 h-20 rounded-full bg-background hover:bg-card flex items-center justify-center shadow-xl transition-all hover:scale-105"
              >
                <Mic className="w-8 h-8 text-on-primary-text" />
              </button>
              <p className="text-sm text-muted-foreground">
                <Trans>Tap the mic to start dictation</Trans>
              </p>
            </div>
          )}

          {/* === RECORDING === */}
          {modalState === 'recording' && (
            <div className="flex flex-col gap-3">
              {/* Timer + pulse */}
              <div className="flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-destructive animate-pulse" />
                <span className="text-2xl font-mono font-bold text-[color:rgb(var(--text-title))]">
                  {formatTime(elapsedTime)}
                </span>
              </div>

              {/* Live transcript */}
              <div className="min-h-[60px] max-h-[150px] overflow-y-auto p-3 bg-muted/50 rounded-xl border text-sm">
                {transcript || interimTranscript ? (
                  <p className="whitespace-pre-wrap text-[color:rgb(var(--text-body))] leading-relaxed">
                    {transcript}
                    {interimTranscript && (
                      <span className="text-muted-foreground"> {interimTranscript}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-muted-foreground text-center">
                    <Trans>Listening...</Trans>
                  </p>
                )}
              </div>

              {/* Stop button */}
              <div className="flex justify-center">
                <button
                  onClick={handleStopRecording}
                  className="w-14 h-14 rounded-full bg-destructive hover:bg-destructive/90 flex items-center justify-center shadow-lg transition-all"
                >
                  <div className="w-5 h-5 rounded-sm bg-background" />
                </button>
              </div>
            </div>
          )}

          {/* === POLISHING === */}
          {modalState === 'polishing' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-[color:rgb(var(--text-body))]">
                  <IconSparkles className="w-4 h-4 inline mr-1 text-purple-500" />
                  <Trans>AI is polishing your text...</Trans>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  <Trans>Fixing recognition errors and making it natural</Trans>
                </p>
              </div>
            </div>
          )}

          {/* === REVIEW === */}
          {modalState === 'review' && (
            <div className="flex flex-col gap-3">
              {/* Polished text */}
              <div className="min-h-[60px] max-h-[200px] overflow-y-auto p-3 bg-background rounded-xl border text-sm whitespace-pre-wrap text-[color:rgb(var(--text-body))] leading-relaxed">
                {showRaw ? rawText : polishedText}
              </div>

              {/* Toggle raw/polished */}
              {rawText !== polishedText && (
                <button
                  onClick={() => setShowRaw(!showRaw)}
                  className="self-start flex items-center gap-1 text-xs text-muted-foreground hover:text-[color:rgb(var(--text-body))] transition-colors"
                >
                  <Undo2 className="w-3 h-3" />
                  {showRaw ? <Trans>Show AI-polished</Trans> : <Trans>Show original</Trans>}
                </button>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleStartRecording}
                >
                  <Mic className="w-4 h-4 mr-1" />
                  <Trans>Re-record</Trans>
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleSend}
                >
                  <Check className="w-4 h-4 mr-1" />
                  <Trans>Send</Trans>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {customButton ? (
        <div onClick={() => showModal()}>{customButton}</div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => showModal()}
          className="h-8 w-8 rounded-full bg-secondary hover:bg-secondary/80"
          data-cy="recorder-btn"
        >
          <Mic className="h-5 w-5 text-muted-foreground" />
        </Button>
      )}
      {show && createPortal(modalContent, document.body)}
    </>
  );
};

export class VoiceRecordModalEmitter {
  private static emitter = new EventEmitter();

  static startRecording() {
    this.emitter.emit("start");
  }

  static subscribe(handler: () => void): () => void {
    this.emitter.on("start", handler);
    return () => this.emitter.removeAllListeners();
  }
}

export default MsgRecordModal;